import React from 'react';
import { useRobloky } from '../../context/RoblokyContext';
import { useAuth } from '../../context/AuthContext';
import { basePrefix } from '../../utils/format';
import {
  Bookmark,
  User as UserIcon,
  Shield,
  LogIn,
  LogOut,
} from 'lucide-react';

export const Navbar: React.FC = () => {
  const {
    view,
    goHome,
    openSaved,
    openProfile,
    profile,
    isAdmin,
    setIsAdminPanelOpen,
    openAuthModal,
    gamesQuery,
    setGamesQuery,
  } = useRobloky();
  const { user: firebaseUser, signOut } = useAuth();

  return (
    <header className="sticky top-0 z-40 w-full border-b border-white/10 bg-[#090b10]/95 backdrop-blur-md">
      <div className="mx-auto flex h-14 sm:h-16 max-w-7xl items-center justify-between gap-2 px-3 sm:px-6">
        <button onClick={goHome} className="flex items-center gap-2 text-left group focus:outline-none shrink-0">
          <span className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg overflow-hidden border border-red-500/30 group-hover:border-red-400 transition-all">
            <img src={`${basePrefix()}/logo.svg`} alt="Robloky logo" className="h-full w-full object-cover" />
          </span>
          <span className="font-display text-lg sm:text-xl font-bold tracking-wider text-white group-hover:text-red-400 transition-colors whitespace-nowrap">
            ROBLOKY
          </span>
        </button>

        <div className="hidden md:flex flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search games, creators, genres..."
            value={gamesQuery}
            onChange={(e) => {
              if (view.name !== 'home') goHome();
              setGamesQuery(e.target.value);
            }}
            className="w-full px-4 py-2 rounded-xl bg-black/40 border border-white/10 text-xs text-white placeholder-slate-500 focus:border-red-500 focus:outline-none"
          />
        </div>

        <nav className="hidden md:flex items-center gap-1">
          <button
            onClick={goHome}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              view.name === 'home' || view.name === 'game'
                ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                : 'text-slate-400 hover:text-slate-200 border border-transparent'
            }`}
          >
            Games
          </button>
          <button
            onClick={openSaved}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              view.name === 'saved'
                ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                : 'text-slate-400 hover:text-slate-200 border border-transparent'
            }`}
          >
            <Bookmark className="h-4 w-4" />
            Saved
          </button>
          {firebaseUser && (
            <button
              onClick={openProfile}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                view.name === 'profile'
                  ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                  : 'text-slate-400 hover:text-slate-200 border border-transparent'
              }`}
            >
              <UserIcon className="h-4 w-4" />
              Profile
            </button>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => setIsAdminPanelOpen(true)}
              title="Scout admin console"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-slate-900/90 border border-red-500/40 text-red-300 text-xs font-mono hover:bg-red-950/60 transition-all"
            >
              <Shield className="h-3.5 w-3.5" />
              <span className="font-bold hidden sm:inline">Scout</span>
            </button>
          )}
          {!firebaseUser ? (
            <button
              onClick={() => openAuthModal('signin')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-400 text-white font-bold text-xs transition-all whitespace-nowrap"
            >
              <LogIn className="h-3.5 w-3.5" />
              <span>Sign In</span>
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <button
                onClick={openProfile}
                className="flex items-center gap-2 pl-1.5 pr-2.5 py-1 rounded-full bg-slate-900/80 border border-white/10 hover:border-red-500/50 transition-all"
                title={profile?.displayName || 'Profile'}
              >
                {profile?.avatar ? (
                  <img src={profile.avatar} alt="" className="h-6 w-6 sm:h-7 sm:w-7 rounded-full object-cover border border-red-400/50" />
                ) : (
                  <span className="flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full bg-red-500/20 border border-red-500/40">
                    <UserIcon className="h-3.5 w-3.5 text-red-300" />
                  </span>
                )}
                <span className="text-xs font-semibold text-slate-200 truncate max-w-[85px] hidden sm:block">
                  {profile?.displayName || 'Gamer'}
                </span>
              </button>
              <button
                onClick={() => signOut()}
                title="Sign Out"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-slate-900/60 text-slate-400 hover:text-rose-400 hover:border-rose-500/40 transition-colors"
              >
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
